import { IComponentOptions } from "angular";
import { EventEmitter } from "../../../common/components/event-emitter";
import { ValidNewBankAccount } from "./add-bank-account.component";
import { DirectDepositAccount, UpdatedDirectDeposit } from "./change-direct-deposit.component";
import { DepositAmountType } from "./deposit-amount-type";
import templateUrl from "./edit-bank-accounts.component.html";
import { LocaleService, EmployeeService, BankAccountCreationPolicyService, BankAccountCreationPolicy } from "../../../core/services/";

/**
 * A component displaying editors for multiple bank accounts.
 */
export const EditBankAccountsComponent: IComponentOptions = {
    templateUrl,
    bindings: {
        accounts: "<",
        employee: "<",
        identity: "<",
        expandedAccount: "<?",
        depositAmountTypes: "<",
        addAccount: "&onAddAccount",
        changeAccount: "&onChangeAccount",
        deleteAccount: "&onDeleteAccount"
    },
    controller: class EditBankAccountsComponent {
        accounts: BankAccountVM[];
        depositAmountTypes: DepositAmountType[];
        mode: "list" | "addNew" = "list";
        newAccount: ValidNewBankAccount = null;
        // The ID for a bank account that should be expanded in the view.
        expandedAccount: number | null;
        addAccount: EventEmitter<{ account: ValidNewBankAccount }>;
        changeAccount: EventEmitter<{ account: UpdatedDirectDeposit }>;
        deleteAccount: EventEmitter<{ accountID: number }>;
        bankAccountCreationPolicy: BankAccountCreationPolicy;
        employee: any;

        constructor(private localeService: LocaleService, private employeeService: EmployeeService,
            private bankAccountCreationPolicyService: BankAccountCreationPolicyService) {
            'ngInject'
        }

        $onInit() {
            this.retrieveBankAccountPolicy();
        }

        onAddDirectDeposit() {
            this.mode = "addNew";
        }

        onNewDirectDepositChange({ account }: { account: ValidNewBankAccount }) {
            this.newAccount = account;
        }

        onChangeDirectDeposit({ account }: { account: UpdatedDirectDeposit }) {
            this.changeAccount({
                $event: {
                    account
                }
            });
        }

        onSaveNewDirectDeposit() {
            this.addAccount({
                $event: {
                    account: this.newAccount
                }
            });
        }

        onRemoveBankAccount(account: BankAccountVM) {
            if (!confirm("The bank account will be deleted. Are you sure?")) {
                return;
            }
            this.deleteAccount({
                $event: {
                    accountID: account.bankAccountID
                }
            });
        }

        getEmployeeProfile(employeeId) {
            return this.employeeService.retrieveEmployeeProfile(employeeId)
                .then(employeeProfile => {
                    return employeeProfile;
                })
        }

        retrieveBankAccountPolicy() {
            this.getEmployeeProfile(this.employee.Id)
                .then((employeeProfile) => {
                    return this.localeService.retrieveLocales()
                        .then(locales => {
                            return locales.filter((locale) => {
                                return locale.countryID === employeeProfile.HomeAddress.CountryID;
                            })[0] || this.localeService.retrieveCurrentLocale();
                        })
                        .then(locale => {
                            return this.localeService.retrieveLocaleTranslations(locale.localeID);
                        })
                        .then(translations => {
                            if (translations) {
                                return this.bankAccountCreationPolicyService
                                    .retrievePolicyByCountryID(employeeProfile.HomeAddress.CountryID)
                                    .then(policy => {
                                        policy.routingNumberLabel = translations.RoutingNumber_Label;
                                        policy.institutionNumberLabel = translations.InstitutionNumber_Label;
                                        policy.voidCheckLabel = translations.VoidCheck_Label;
                                        this.bankAccountCreationPolicy = policy;
                                    })
                            }
                        })
                });
        }
    }
}

export interface BankAccountVM extends DirectDepositAccount {
    /** The API's identifier for the bank account.  */
    bankAccountID: number;
    /** Indicate whether the bank account is expanded in the view. */
    expanded: boolean;
}
